/**
 * Sysadmin Studio Catalog — parent layout (Studio Phase 3D scaffolding).
 *
 * Cross-module declaration: this file lives under /sysadmin/* but is
 * owned by the Studio module per the QA-FIX-COORDINATION protocol —
 * see docs/audit/studio-phases-345-fix-2026-05-08.md "Cross-module
 * touches". The polished sysadmin UI for system catalog management is
 * v1.2; this is intentionally minimal CRUD scaffolding so:
 *   1. The runtime endpoints (Phase 3C) have a real consumer.
 *   2. Sysadmins can edit / promote system tools / skills / prompts /
 *      agents without curl after Phase 3 lands.
 *   3. Phase 4+5 helper agent + tenant flows can rely on the catalog
 *      being populated end-to-end.
 *
 * Tabs: Tools, Skills, Prompts, Agents, Categories. The four artifact
 * tabs each render a list + a Sheet-based create/edit form. The
 * Categories tab manages sysadmin-suggested category defaults.
 */

import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { Wrench, BookOpen, MessageSquare, Bot, Tags, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useReloadSystemCatalogManifest } from '@/lib/use-sysadmin-system-catalog';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/studio')({
  component: SysadminStudioLayout,
});

const TABS = [
  { key: 'tools',      label: 'Tools',      icon: Wrench,         to: '/sysadmin/studio/tools' },
  { key: 'skills',     label: 'Skills',     icon: BookOpen,       to: '/sysadmin/studio/skills' },
  { key: 'prompts',    label: 'Prompts',    icon: MessageSquare,  to: '/sysadmin/studio/prompts' },
  { key: 'agents',     label: 'Agents',     icon: Bot,            to: '/sysadmin/studio/agents' },
  { key: 'categories', label: 'Categories', icon: Tags,           to: '/sysadmin/studio/categories' },
] as const;

function SysadminStudioLayout() {
  const location = useLocation();
  const reload = useReloadSystemCatalogManifest();

  const handleReload = () => {
    reload.mutate(undefined, {
      onSuccess: ({ counts }) => {
        toast.success('Manifest reloaded', {
          description: `Tools ${counts.tools} · Skills ${counts.skills} · Prompts ${counts.prompts} · Agents ${counts.agents} · Categories ${counts.categories}`,
        });
      },
      onError: (err) =>
        toast.error('Reload failed', { description: err.message }),
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="flex items-center justify-between px-6 pt-3">
          <div>
            <h1 className="text-base font-semibold">Studio Catalog (Sysadmin)</h1>
            <p className="text-[11px] text-muted-foreground max-w-2xl mt-0.5">
              Author system-published tools, skills, prompts, and agents that ship to every tenant.
              Scaffolding only — polished UI is a v1.2 pass.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReload}
            disabled={reload.isPending}
            className="gap-1.5"
            title="Re-run the manifest loader (force=true). Diff-aware; sysadmin edits not in the manifest are preserved."
          >
            <RefreshCw className={cn('h-3.5 w-3.5', reload.isPending && 'animate-spin')} />
            Reload manifest
          </Button>
        </div>
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6 mt-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active =
              location.pathname === t.to ||
              location.pathname.startsWith(`${t.to}/`);
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
